import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  forwardRef,
  viewChild,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { AutocompleteFieldComponent } from '../autocomplete-field/autocomplete-field.component';
import { Charity } from '../../../_core/models/charity.model';

const CHARITY_SELECTOR_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CharitySelectorComponent),
  multi: true,
};

@Component({
  selector: 'app-charity-selector',
  standalone: true,
  imports: [AutocompleteFieldComponent],
  providers: [CHARITY_SELECTOR_VALUE_ACCESSOR],
  templateUrl: './charity-selector.component.html',
  styleUrl: './charity-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharitySelectorComponent implements ControlValueAccessor {
  // Inputs
  charities = input<Charity[]>([]);
  required = input<boolean>(false);
  label = input<string>('contribute.section2.charity.label');
  placeholder = input<string>('contribute.section2.charity.placeholder');
  errorMessage = input<string>('contribute.section2.charity.error');

  // State
  disabled = signal(false);

  // Outputs
  charitySearch = output<string>();
  charitySelect = output<Charity>();

  // ViewChild reference
  autocompleteField = viewChild<AutocompleteFieldComponent<Charity>>(AutocompleteFieldComponent);

  // ControlValueAccessor callbacks
  private onChange: (value: Charity | null) => void = () => {};
  private onTouched: () => void = () => {};

  // ControlValueAccessor implementation
  writeValue(value: Charity | null): void {
    const field = this.autocompleteField();
    if (field) {
      field.writeValue(value);
    }
  }

  registerOnChange(fn: (value: Charity | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Event handlers
  onSearch(query: string): void {
    this.charitySearch.emit(query);
  }

  onSelect(charity: Charity): void {
    this.onChange(charity);
    this.charitySelect.emit(charity);
  }
}
